import { css } from 'twin.macro';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { fetchWrapper, getFormDataWithValue, getUserRole, toastWrapper } from '../../../_helpers';
import StaticPreview from './StaticPreview';
import { toast } from 'react-toastify';
import MultiSubmissionView from './MultiSubmissionView';
import { Tab, Tabs } from 'react-bootstrap';
import FileSaver from 'file-saver';
import { FormPdf } from '../../../_elements/FormPdf';
import { getTeamIdFromURL, isAdminOrTeamSettings } from '../../../_helpers';
import { PDFViewer } from '@react-pdf/renderer';
import PdfTemplatePreview from './PdfTemplatePreview';
import SelectedOptionsView from '@/_elements/ViewForm/SelectedOptionsView';
import FormSubmissionModalWrapper from '@/components/Features/UserFormData/FormSubmissionModalWrapper';

export const isAllowedDraftFormType = (form, sharedFormId) => {
    if (sharedFormId) {
        return false;
    }
    return true;
};

const ViewForm = ({
    room,
    match = null,
    formId = null,
    hide = null,
    showSignature,
    setFormInfo,
    canDownload,
    currUser,
    handleSetform = () => {},
}) => {
    const [formData, setFormData] = useState(null);
    const templatePreviewRef = useRef();
    const [isDownloading, setIsDownloading] = useState(false);
    const [loading, setLoading] = useState(true);
    const user = useSelector((state) => state.user.user);
    const teamId = getTeamIdFromURL();
    const isSettings = isAdminOrTeamSettings();
    const userId = user.id || user._id;

    const [key, setKey] = useState('staticform');
    const [form, setForm] = useState({});
    let id = null;
    if (formId) {
        id = formId;
    } else if (match) {
        id = match.params.id;
    }
    const [formWithValue, setFormWithValue] = useState({});

    useEffect(() => {
        const getFormData = async () => {
            setLoading(true);
            try {
                if (id) {
                    await fetchWrapper.get(`userFormData/${id}`).then((res) => {
                        handleSetform(res);
                        let newFormData = Object.assign([], res.formData?.data);
                        let newFormDataWithValue = [];
                        if (newFormData.length > 0) {
                            newFormDataWithValue = getFormDataWithValue(newFormData, res?.data || []);
                            setFormData(newFormDataWithValue);

                            setForm(res);
                            setFormWithValue({ ...(res?.formData || {}), data: newFormDataWithValue });
                            if (setFormInfo) setFormInfo(res.formData);
                        } else {
                            toast.error(
                                res?.status === 403
                                    ? "You don't have permission to view this Form"
                                    : 'Oops! File has been Corrupted.'
                            );
                            if (hide) {
                                hide();
                            }
                        }
                    });
                } else if (match) {
                    await fetchWrapper.get(`forms/${id}`).then((res) => {
                        setFormData(res.data);
                    });
                } else {
                    setFormData(null);
                }
            } catch (error) {
                if (error?.name === 'AbortError') return;
                toastWrapper.error(error?.message || 'Oops! File has been Corrupted.');
                console.log(error);
            } finally {
                setLoading(false);
            }
        };

        getFormData();
    }, [id, hide, match]);

    const showMultipleSubmissionView = useMemo(() => {
        const allowedTypes = ['completed', 'inprogress'];
        return allowedTypes.includes((form || {})?.submissionType);
    }, [form]);

    const generatePdfAndDownload = async (singleFile = true) => {
        setIsDownloading(true);
        try {
            if (singleFile) {
                const blob = await FormPdf([form], 'combined');
                return FileSaver.saveAs(
                    blob,
                    `${form?.formData?.name || 'submittedForm'}_${form?.userData?.name || 'user'}.pdf`
                );
            }
        } catch (error) {
            console.log('ERROR IN PDF GENERATION => ', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const downloadTemplatePdf = async () => {
        try {
            setIsDownloading(true);
            if (templatePreviewRef.current) {
                await templatePreviewRef.current.download();
            }
        } catch (error) {
            console.log('ERROR IN PDF TEMPLATE GENERATION => ', error);
        } finally {
            setIsDownloading(false);
        }
    };
    const canDownloadPDF = () => {
        if (currUser?.role === 'admin') {
            return true;
        }
        const teamRole = currUser?.teamList?.find(
            (teamObj) => teamObj?.team?.id === teamId || teamObj?.team?._id === teamId
        )?.role;
        if (['owner', 'teamManager'].includes(teamRole)) {
            return true;
        }
        return false;
    };

    const isDraft = isAllowedDraftFormType(form) && form?.submissionType === 'draft';
    const canComplete =
        ['admin', 'owner', 'teamManager', 'supervisor'].includes(getUserRole(currUser, form?.team, form?.chatroom)) ||
        form?.user === userId ||
        (form?.visibleTo || []).includes('user');
    const canSubmit = canComplete;

    if (loading) {
        return (
            <div
                css={css`
                    width: 100%;
                    min-height: 50vh;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                `}
            >
                <img src="/images/ball-triangle.svg" alt="loader" />
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex flex-column">
                {(canDownload || canDownloadPDF()) && (
                    <div>
                        {isDownloading ? (
                            <p className="float-right">Downloading...</p>
                        ) : (
                            <div className="d-flex justify-content-end">
                                <button
                                    onClick={() => generatePdfAndDownload()}
                                    className="btn btn-info float-right border border-white btn-lg mb-2"
                                >
                                    Download PDF
                                </button>
                                {form?.formData?.pdfTemplate?.url && key === 'teamplatepdf' && (
                                    <button
                                        onClick={() => downloadTemplatePdf()}
                                        className="btn btn-info float-right border border-white btn-lg mb-2"
                                    >
                                        Download Template PDF
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <Tabs
                    activeKey={key}
                    onSelect={(k) => setKey(k)}
                    className="mb-3"
                    css={css`
                        .active {
                            .tab-title {
                                color: #495057 !important;
                            }
                        }
                    `}
                >
                    <Tab
                        eventKey="staticform"
                        title={
                            <span className="tab-title" id="viewform.staticform">
                                Static Form
                            </span>
                        }
                    >
                        {key === 'staticform' && (
                            <FormSubmissionModalWrapper
                                currRoom={room}
                                formElements={formData}
                                onlyView={Boolean(formId)}
                                isPreview={!isDraft}
                                userFormData={form || {}}
                                handleCloseModal={hide}
                                isDraft={isDraft}
                                canSubmit={canSubmit}
                                canComplete={canComplete}
                                form={formWithValue}
                                submissionType={form?.submissionType}
                            />
                        )}
                    </Tab>
                    {(canDownload || canDownloadPDF()) && (
                        <Tab eventKey="pdf" title={<span className="tab-title">PDF</span>}>
                            {key === 'pdf' && (
                                <div
                                    css={css`
                                        height: 70vh;
                                        overflow: auto;
                                    `}
                                >
                                    <PDFViewer
                                        width="100%"
                                        height="100%"
                                        css={css`
                                            border: none;
                                            outline: none;
                                        `}
                                        showToolbar={false}
                                    >
                                        {FormPdf(form, 'component')}
                                    </PDFViewer>
                                </div>
                            )}
                        </Tab>
                    )}
                    {(canDownload || canDownloadPDF()) && (
                        <Tab
                            eventKey="selected-options-view"
                            title={<span className="tab-title">Completed Fields PDF</span>}
                        >
                            {key === 'selected-options-view' && (
                                <SelectedOptionsView
                                    formData={formData || []}
                                    form={form || {}}
                                    canDownload={canDownload}
                                    canDownloadPDF={canDownloadPDF}
                                    handleCloseModal={hide}
                                />
                            )}
                        </Tab>
                    )}
                    {form?.formData?.pdfTemplate?.url && (
                        <Tab eventKey="teamplatepdf" title={<span className="tab-title">Template PDF</span>}>
                            {key === 'teamplatepdf' && (
                                <PdfTemplatePreview
                                    width="100%"
                                    height="100%"
                                    css={css`
                                        border: none;
                                        outline: none;
                                    `}
                                    formTemplateUrl={form?.formData?.pdfTemplate?.url}
                                    formElements={formData}
                                    ref={templatePreviewRef}
                                    formName={`${form?.formData?.name || 'submittedForm'}___${form?.teamData?.name || 'team'}`}
                                    pdfDimension={form?.formData?.pdfTemplate?.metadata?.dimensions || {}}
                                    canDownload={canDownload || canDownloadPDF()}
                                ></PdfTemplatePreview>
                            )}
                        </Tab>
                    )}
                </Tabs>
            </div>
            {showMultipleSubmissionView && (
                <MultiSubmissionView showSignature={showSignature} form={form} userId={userId} hide={hide} id={id} />
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    currUser: state.user.user,
});

export default connect(mapStateToProps, {})(ViewForm);
